import React, { useState, useEffect } from 'react';
import { 
    Plus, Edit3, Trash2, CheckCircle, AlertTriangle, Building2, Settings 
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';
import '../Dashboard.css';

const CreationSociete = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const company_id = user?.company_id || user?.companyId;

  const [societes, setSocietes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    nom: '',
    sigle: '',
    forme_juridique: 'SARL',
    numero_contribuable: '',
    numero_rccm: '',
    adresse: '',
    ville: '',
    pays: 'Togo',
    telephone: '',
    email: '',
    nom_contact: '',
    telephone_contact: '',
    email_contact: '',
    observations: '',
    is_active: 1
  });

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [modalConfig, setModalConfig] = useState({ show: false, action: null, message: '', id: null });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchSocietes = async () => {
    try {
      setLoading(true);
      const res = await API.get('/hebergement/societes').catch(() => ({ data: [] }));
      const rawData = res.data;
      let list = [];
      if (Array.isArray(rawData)) {
        list = rawData;
      } else if (rawData && typeof rawData === 'object') {
        list = rawData.societes || rawData.data || rawData.items || [];
      }
      setSocietes(list);
    } catch (err) {
      console.error("Erreur chargement sociétés:", err);
      showToast("Erreur de chargement des sociétés", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSocietes();
  }, [company_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nom.trim()) {
      showToast("Le nom de la société est obligatoire", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        nom: formData.nom.trim(),
        sigle: formData.sigle ? formData.sigle.trim() : null,
        numero_contribuable: formData.numero_contribuable ? formData.numero_contribuable.trim() : null,
        numero_rccm: formData.numero_rccm ? formData.numero_rccm.trim() : null,
        is_active: Number(formData.is_active)
      };

      if (editingId) {
        await API.put(`/hebergement/societes/${editingId}`, payload);
        showToast("Société mise à jour avec succès");
      } else {
        await API.post('/hebergement/societes', payload);
        showToast("Société créée avec succès");
      }

      closeForm();
      fetchSocietes();
    } catch (err) {
      console.error("Erreur enregistrement société :", err);
      const errorMsg = err.response?.data?.error || "Erreur lors de l'enregistrement";
      showToast(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (item) => {
    setEditingId(item.id);
    setFormData({
      nom: item.nom || '',
      sigle: item.sigle || '',
      forme_juridique: item.forme_juridique || 'SARL',
      numero_contribuable: item.numero_contribuable || '',
      numero_rccm: item.numero_rccm || '',
      adresse: item.adresse || '',
      ville: item.ville || '',
      pays: item.pays || 'Togo',
      telephone: item.telephone || '',
      email: item.email || '',
      nom_contact: item.nom_contact || '',
      telephone_contact: item.telephone_contact || '',
      email_contact: item.email_contact || '',
      observations: item.observations || '',
      is_active: item.is_active !== undefined ? item.is_active : 1
    });
    setIsAdding(true);
  };

  const handleDeleteClick = (id) => {
    setModalConfig({
      show: true,
      action: () => executeDelete(id),
      message: "Voulez-vous vraiment supprimer cette société ?",
      id
    });
  };

  const executeDelete = async (id) => {
    setLoading(true);
    try {
      await API.delete(`/hebergement/societes/${id}`);
      showToast("Société supprimée avec succès");
      setModalConfig({ show: false, action: null, message: '', id: null });
      fetchSocietes();
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Impossible de supprimer cette société (des séjours y sont rattachés).";
      showToast(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const closeForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({
      nom: '', sigle: '', forme_juridique: 'SARL', numero_contribuable: '', numero_rccm: '',
      adresse: '', ville: '', pays: 'Togo', telephone: '', email: '',
      nom_contact: '', telephone_contact: '', email_contact: '', observations: '', is_active: 1
    });
  };

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar />
      <main style={{ flex: 1, overflowY: 'auto', background: '#f1f5f9', position: 'relative' }}>
        
        {toast.show && (
          <div style={{...toastContainerStyle, background: toast.type === 'success' ? '#10b981' : '#ef4444'}}>
            {toast.type === 'success' ? <CheckCircle size={18}/> : <AlertTriangle size={18}/>}
            {toast.message}
          </div>
        )}

        <header style={headerStyle}>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Building2 size={22} color="#2563eb" /> GESTION DES SOCIÉTÉS (CORPORATE / B2B)
            </h1>
            <div style={{ color: '#64748b', fontSize: '13px' }}>
              Répertoire des entreprises partenaires pour la prise en charge et la facturation des séjours
            </div>
          </div>
          <div>
            {!isAdding && !editingId && (
              <button onClick={() => setIsAdding(true)} style={btnPrimaryStyle}>
                <Plus size={18} /> Nouvelle Société
              </button>
            )}
          </div>
        </header>

        <div style={{ padding: '30px 40px' }}>

          {(isAdding || editingId) && (
            <div style={{ ...contentCardStyle, padding: '25px', marginBottom: '25px', border: '2px solid #2563eb' }}>
              <h3 style={{ fontSize: '15px', marginBottom: '15px', fontWeight: '900', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Settings size={18} color="#2563eb"/> {editingId ? 'Modifier les informations de la société' : 'Enregistrer une nouvelle société'}
              </h3>

              <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '15px' }}>
                  <div style={{ flex: 2, minWidth: '220px' }}>
                    <label style={labelStyle}>NOM DE LA SOCIÉTÉ *</label>
                    <input 
                      type="text" 
                      style={inputStyle} 
                      placeholder="Ex: ABC TOGO SARL"
                      value={formData.nom} 
                      onChange={(e) => setFormData({...formData, nom: e.target.value})} 
                      required 
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: '120px' }}>
                    <label style={labelStyle}>SIGLE</label>
                    <input 
                      type="text" 
                      style={inputStyle} 
                      placeholder="Ex: ABC"
                      value={formData.sigle} 
                      onChange={(e) => setFormData({...formData, sigle: e.target.value})} 
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: '130px' }}>
                    <label style={labelStyle}>FORME JURIDIQUE</label>
                    <select 
                      style={inputStyle} 
                      value={formData.forme_juridique} 
                      onChange={(e) => setFormData({...formData, forme_juridique: e.target.value})}
                    >
                      <option value="SARL">SARL</option>
                      <option value="SA">SA</option>
                      <option value="SAS">SAS</option>
                      <option value="SUARL">SUARL</option>
                      <option value="ONG">ONG</option>
                      <option value="ASSOCIATION">Association</option>
                      <option value="AUTRE">Autre</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '15px' }}>
                  <div style={{ flex: 1, minWidth: '160px' }}>
                    <label style={labelStyle}>N° CONTRIBUABLE (NIF)</label>
                    <input 
                      type="text" 
                      style={inputStyle} 
                      placeholder="Numéro fiscal"
                      value={formData.numero_contribuable} 
                      onChange={(e) => setFormData({...formData, numero_contribuable: e.target.value})} 
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: '160px' }}>
                    <label style={labelStyle}>N° RCCM</label>
                    <input 
                      type="text" 
                      style={inputStyle} 
                      placeholder="Registre du commerce"
                      value={formData.numero_rccm} 
                      onChange={(e) => setFormData({...formData, numero_rccm: e.target.value})} 
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    <label style={labelStyle}>STATUT</label>
                    <select 
                      style={inputStyle} 
                      value={formData.is_active} 
                      onChange={(e) => setFormData({...formData, is_active: Number(e.target.value)})}
                    >
                      <option value={1}>Actif</option>
                      <option value={0}>Inactif</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '15px' }}>
                  <div style={{ flex: 2, minWidth: '200px' }}>
                    <label style={labelStyle}>ADRESSE</label>
                    <input 
                      type="text" 
                      style={inputStyle} 
                      placeholder="Quartier, Rue..."
                      value={formData.adresse} 
                      onChange={(e) => setFormData({...formData, adresse: e.target.value})} 
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: '130px' }}>
                    <label style={labelStyle}>VILLE</label>
                    <input 
                      type="text" 
                      style={inputStyle} 
                      placeholder="Ex: Lomé"
                      value={formData.ville} 
                      onChange={(e) => setFormData({...formData, ville: e.target.value})} 
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: '130px' }}>
                    <label style={labelStyle}>PAYS</label>
                    <input 
                      type="text" 
                      style={inputStyle} 
                      value={formData.pays} 
                      onChange={(e) => setFormData({...formData, pays: e.target.value})} 
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '15px' }}>
                  <div style={{ flex: 1, minWidth: '150px' }}>
                    <label style={labelStyle}>TÉLÉPHONE SOCIÉTÉ</label>
                    <input 
                      type="text" 
                      style={inputStyle} 
                      placeholder="+228 ..."
                      value={formData.telephone} 
                      onChange={(e) => setFormData({...formData, telephone: e.target.value})} 
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: '180px' }}>
                    <label style={labelStyle}>EMAIL SOCIÉTÉ</label>
                    <input 
                      type="email" 
                      style={inputStyle} 
                      placeholder="contact@societe.com"
                      value={formData.email} 
                      onChange={(e) => setFormData({...formData, email: e.target.value})} 
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '15px' }}>
                  <div style={{ flex: 1.5, minWidth: '180px' }}>
                    <label style={labelStyle}>PERSONNE DE CONTACT</label>
                    <input 
                      type="text" 
                      style={inputStyle} 
                      placeholder="Nom du responsable / comptable"
                      value={formData.nom_contact} 
                      onChange={(e) => setFormData({...formData, nom_contact: e.target.value})} 
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: '150px' }}>
                    <label style={labelStyle}>TÉLÉPHONE CONTACT</label>
                    <input 
                      type="text" 
                      style={inputStyle} 
                      placeholder="Mobile direct"
                      value={formData.telephone_contact} 
                      onChange={(e) => setFormData({...formData, telephone_contact: e.target.value})} 
                    />
                  </div>
                  <div style={{ flex: 1.5, minWidth: '180px' }}>
                    <label style={labelStyle}>EMAIL CONTACT</label>
                    <input 
                      type="email" 
                      style={inputStyle} 
                      placeholder="email@contact.com"
                      value={formData.email_contact} 
                      onChange={(e) => setFormData({...formData, email_contact: e.target.value})} 
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>OBSERVATIONS</label>
                  <input 
                    type="text" 
                    style={inputStyle} 
                    placeholder="Instructions particulières de facturation..."
                    value={formData.observations} 
                    onChange={(e) => setFormData({...formData, observations: e.target.value})} 
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button type="submit" disabled={loading} style={btnSubmitStyle}>{loading ? '...' : 'Enregistrer la société'}</button>
                  <button type="button" onClick={closeForm} style={btnCancelStyle}>Annuler</button>
                </div>
              </form>
            </div>
          )}

          {/* TABLEAU GLOBAL */}
          <div style={contentCardStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={thStyle}>Société</th>
                  <th style={thStyle}>Forme / Sigle</th>
                  <th style={thStyle}>N° Contribuable</th>
                  <th style={thStyle}>Contact & Téléphone</th>
                  <th style={thStyle}>Statut</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {societes.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Aucune société enregistrée.
                    </td>
                  </tr>
                ) : (
                  societes.map((item, idx) => {
                    const uniqueKey = item.id ? `societe-${item.id}-${idx}` : `societe-idx-${idx}`;
                    return (
                      <tr key={uniqueKey} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ ...tdStyle, fontWeight: '800', color: '#1e293b' }}>
                          {item.nom}
                          <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>{item.ville}, {item.pays}</div>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontWeight: '700', color: '#0369a1' }}>{item.forme_juridique || 'SARL'}</span>
                          {item.sigle && <span style={{ color: '#64748b', fontSize: '11px' }}> ({item.sigle})</span>}
                        </td>
                        <td style={tdStyle}><code style={codeStyle}>{item.numero_contribuable || '---'}</code></td>
                        <td style={tdStyle}>
                          <div>{item.nom_contact || '---'}</div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>{item.telephone || item.telephone_contact || '---'}</div>
                        </td>
                        <td style={tdStyle}>
                          <span style={badgeStyle(item.is_active)}>
                            {item.is_active === 1 ? 'ACTIF' : 'INACTIF'}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button onClick={() => handleEditClick(item)} style={actionBtnStyle('#2563eb')} title="Modifier">
                              <Edit3 size={14}/> Modifier
                            </button>
                            <button onClick={() => handleDeleteClick(item.id)} style={actionBtnStyle('#ef4444')} title="Supprimer">
                              <Trash2 size={14}/> Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {modalConfig.show && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
              <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px', borderRadius: '50%' }}><AlertTriangle size={24} /></div>
              <h2 style={{ margin: 0, fontSize: '18px' }}>Confirmation de suppression</h2>
            </div>
            <p style={{ color: '#475569', marginBottom: '30px' }}>{modalConfig.message}</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setModalConfig({ show: false, action: null, message: '', id: null })} style={btnSecondaryStyle}>Annuler</button>
              <button onClick={modalConfig.action} style={btnDangerStyle}>{loading ? '...' : 'Supprimer'}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes slideInToast { from { transform: translate(-50%, -20px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }`}</style>
    </div>
  );
};

// --- STYLES ---
const headerStyle = { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const contentCardStyle = { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'hidden' };
const btnPrimaryStyle = { background: '#2563eb', color: 'white', border: 'none', padding: '10px 22px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '700' };
const btnSecondaryStyle = { border: '1px solid #e2e8f0', padding: '10px 15px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#475569', background: 'white' };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', marginTop: '4px', background: '#fff', boxSizing: 'border-box' };
const labelStyle = { fontSize: '11px', fontWeight: '900', color: '#475569' };
const thStyle = { padding: '15px 20px', fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '800', textAlign: 'left' };
const tdStyle = { padding: '12px 20px', fontSize: '13px', color: '#334155' };
const codeStyle = { fontSize: '12px', background: '#f1f5f9', padding: '4px 8px', borderRadius: '4px', color: '#2563eb', fontWeight: '900' };
const btnSubmitStyle = { padding: '10px 25px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' };
const btnCancelStyle = { padding: '10px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' };
const actionBtnStyle = (color) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${color}30`, background: `${color}10`, color: color, fontSize: '11px', fontWeight: '900', cursor: 'pointer' });
const badgeStyle = (is_active) => {
  const bg = is_active === 1 ? '#dcfce7' : '#fee2e2';
  const color = is_active === 1 ? '#15803d' : '#b91c1c';
  return { padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: '900', background: bg, color: color };
};
const toastContainerStyle = { position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, color: 'white', padding: '12px 24px', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '800', animation: 'slideInToast 0.3s ease-out' };
const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalContentStyle = { background: 'white', padding: '30px', borderRadius: '12px', width: '400px', maxWidth: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' };
const btnDangerStyle = { background: '#ef4444', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' };

export default CreationSociete;